import { useEffect, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import {
    DEFAULT_PAGE_COUNT,
    ratingColors,
    ratingText,
    BORDER_WIDTH,
    badge,
    wrap, fmtScore } from './Constants';
import { prepareChartData } from './prepareChartData';
import { METRIC_KEYS, METRIC_LABELS } from './Constants';
import { renderDesignBands } from './designBandOverlay';
import { verdictFromLenses, BAND_STYLE } from './verdictModel';
import { unitDisplayLabel } from './unitLabels';

// ── Level-highlight channel ────────────────────────────────────────────────
// When a concrete grain rollup is the home view (e.g. the Scenes rollup), the
// page soft-highlights EVERY wedge of that grain on the wheel so the writer
// sees which ring the rollup describes. The list of that grain's node ids is
// threaded here as a tiny module-level store rather than a new prop, because
// ScriptSnout renders SunburstChart (which can't forward the prop) — ScriptSnout
// calls setLevelHighlight(ids); the chart hook subscribes via useLevelHighlight.
let _levelIds = [];
const _levelSubs = new Set();
export function setLevelHighlight(ids) {
  const next = Array.isArray(ids) ? ids : [];
  // cheap identity check so we don't notify (and re-render the overlay) on no-op
  if (next.length === _levelIds.length && next.every((v, i) => v === _levelIds[i])) return;
  _levelIds = next;
  _levelSubs.forEach(fn => { try { fn(_levelIds); } catch (e) { /* noop */ } });
}
function useLevelHighlight() {
  const [ids, setIds] = useState(_levelIds);
  useEffect(() => {
    const fn = next => setIds(next);
    _levelSubs.add(fn);
    setIds(_levelIds); // sync on mount in case it changed before subscribing
    return () => { _levelSubs.delete(fn); };
  }, []);
  return ids;
}

export default function useSunburstChart({ movie, includeScenes, metric, dimNotOwed = true, onSelect, animateAct1, selectedId, autoSelectRoot = true, levelHighlight, interactive = true, size }) {
  const subbedLevelIds = useLevelHighlight();
  // A second, STATIC instance (the Zone A teaching ring) passes its own levelHighlight so it does
  // NOT share the module-global highlight channel with the live wheel. The live wheel omits the prop
  // and keeps subscribing to the global (existing behaviour).
  const levelIds = levelHighlight !== undefined ? levelHighlight : subbedLevelIds;
  const levelIdsRef = useRef(levelIds);
  levelIdsRef.current = levelIds;
  // export default function useSunburstChart({ movie, includeScenes, onSelect, animateAct1 }) {
  const ref = useRef();
  const clickTracker = useRef({ id: null, time: 0, count: 0 });
  const currentLevelRef = useRef(null); // Track current zoom level
  const selectedNodeRef = useRef(null); // Track the currently selected node
  const chartDataRef = useRef(null); // Store the current chart data
  const overlayNodesRef = useRef(null); // overlay node list, for external (rollup-driven) highlighting
const hasInitialized = useRef(false); // NEW: Track if we've done initial setup

  useEffect(() => {
    if (!movie) return;

    // const { nodes } = prepareChartData(movie, includeScenes);
    const { nodes } = prepareChartData(movie, includeScenes, metric, dimNotOwed);
    
    // Store the nodes for later reference
    chartDataRef.current = { nodes };

    const ids = [];
    const labels = [];
    const parents = [];
    const values = [];
    const colors = [];
    const borders = [];
    const customdata = [];

    // Set default styling
    const DEFAULT_BORDER_COLOR = 'rgb(0, 0, 0)';
    const DEFAULT_BORDER_WIDTH = 2;
    const HIGHLIGHT_BORDER_COLOR = 'rgb(255, 255, 255)';
    const HIGHLIGHT_BORDER_WIDTH = 4;
    
    nodes.forEach(node => {
      ids.push(node.id);
      labels.push(node.formattedLabel || node.label);
      parents.push(node.parent);
      values.push(node.value);
      colors.push(node.color || '#cccccc');
      borders.push(DEFAULT_BORDER_COLOR);
      
      customdata.push(createCustomData(node, movie, metric, dimNotOwed));
    });

    const textSizes = [];

    nodes.forEach(node => {
      if (node.nodeType === 'movie' || node.nodeType === 'act') {
        textSizes.push(25); // Big text for movie & act
      } else if (node.nodeType === 'sequence') {
        textSizes.push(18); // Medium for sequences
      } else if (node.nodeType === 'scene') {
        textSizes.push(12); // Normal for scenes
      } else {
        textSizes.push(10); // Gaps or unknowns
      }
    });
        
    // Create a map of node IDs to determine which nodes have children
    const nodeChildrenMap = {};
    nodes.forEach(node => {
      nodeChildrenMap[node.id] = [];
    });
    nodes.forEach(node => {
      if (node.parent && nodeChildrenMap[node.parent]) {
        nodeChildrenMap[node.parent].push(node.id);
      }
    });
    
    // Function to check if a node is a leaf (has no children)
    function isLeafNode(nodeId) {
      return !nodeChildrenMap[nodeId] || nodeChildrenMap[nodeId].length === 0;
    }

    const data = [{
      type: 'sunburst',
      ids,
      labels,
      parents,
      values,
      textinfo: 'label',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata,
      textfont: {
        size: textSizes,
        color: '#000'
      },
      marker: {
        colors,
        line: {
          color: borders,
          width: Array(ids.length).fill(DEFAULT_BORDER_WIDTH)
        }
      },
      // Plotly fades LEAF (scene) wedges to 0.7 opacity by default — that's why the outer ring
      // read pinker/paler than the inner rings for the SAME band colour. Pin leaves to full opacity
      // so a scene's colour matches its act/sequence exactly.
      leaf: { opacity: 1 },
      branchvalues: 'total',
      sort: false,
      maxdepth: includeScenes ? 4 : 3,
      rotation: 90
    }];

    const layout = {
      margin: { l: 0, r: 0, t: 0, b: 0 },
      // Size the wheel to the column WIDTH (a big square), not the visible height — the
      // left column scrolls, so the wheel needn't be squeezed into the fold. Capped so it
      // stays sane on very wide monitors.
      // A fixed `size` (the compact Zone A teaching ring) overrides the width-driven sizing so it
      // doesn't get forced to the 420px minimum the live wheel uses.
      height: size || Math.max(420, Math.min(ref.current?.clientWidth || 600, 860)),
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      dragmode: false,
      showlegend: false,
      clickmode: '',
      hoverlabel: {
              bgcolor: '#fdfdfb',          // <- pure white
              bordercolor: '#cbd5e1',      // soft grey border
              font: { color: '#0f172a' },  // near-black text
            },      transition: {
        duration: 500,
        easing: 'cubic-in-out'
      }
    };

    const config = { 
      displayModeBar: false, 
      responsive: true
    };

    Plotly.react(ref.current, data, layout, config);

    // ── Dual-color overlay: execution = Plotly's wedge fill (core); design =
    // a thin frame the overlay paints around each scene. The overlay matches
    // slices to nodes by id (via slice.__data__), so DOM slice order doesn't
    // matter. It is pointer-events:none, so clicks (incl. double-click zoom)
    // pass through to Plotly's wedges untouched.
    //
    // The design frame is METRIC-GATED: it encodes the design score, which is
    // only coherent when the fill encodes execution — i.e. metric === 'overall'.
    // On any other metric (pacing, emotion, …) the fill shows that metric, so
    // a design frame would pair two unrelated encodings; we null the scores
    // instead of skipping renderDesignBands entirely because the overlay also
    // draws the level-separator rings and the selection focus ring, which must
    // persist on every metric.
    // Overview encoding (metric === 'overall'): design = band/border, execution
    // = fill, Read = glyph. All three are gated to overview because each pairs
    // with the execution fill; when a specific metric is selected the fill shows
    // that metric, so the design band + read glyph are suppressed (the overlay
    // still draws separator rings + the selection focus ring on every metric).
    // Design-border frame belongs to the D&E view (border = Design, fill = Execution).
    // The Read glyph stays on the integrated Overall view as an extra read cue.
    const showDesignFrames = metric === 'de';
    const showReadGlyphs   = metric === 'overall';
    const sceneOverlayNodes = nodes.map(n => ({
      nodeType: n.nodeType,
      id: n.id,
      // act labels for the rim annotation (overlay draws them around the outer edge)
      label: n.nodeType === 'act' ? (n.formattedLabel || n.label || null) : null,
      design_score: showDesignFrames ? (n.detailedContent?.design ?? null) : null,
      read_score:   showReadGlyphs ? (n.detailedContent?.read ?? null) : null,
    }));
    overlayNodesRef.current = sceneOverlayNodes; // expose for external highlight effect
    renderDesignBands(ref.current, sceneOverlayNodes, selectedNodeRef.current, levelIdsRef.current, metric === 'de');
    // plotly_afterplot fires after every redraw (zoom, resize, animation
    // completion), so the overlay always re-aligns and keeps the focus ring.
    const afterPlotHandler = () => renderDesignBands(ref.current, sceneOverlayNodes, selectedNodeRef.current, levelIdsRef.current, metric === 'de');
    ref.current.on('plotly_afterplot', afterPlotHandler);

    // Selection is now drawn by the overlay as a focus ring (blue + white halo)
    // around the selected wedge. This is reliable (matched by id, redrawn on
    // every Plotly redraw) and — unlike the old white marker.line restyle — it
    // doesn't fight Plotly's per-wedge borders, isn't hidden under the design
    // frame, and avoids a Plotly.restyle (a full redraw) on every click.
    function highlightNode(nodeId) {
      if (!nodeId) return;
      selectedNodeRef.current = nodeId;
      renderDesignBands(ref.current, sceneOverlayNodes, nodeId, levelIdsRef.current, metric === 'de');
    }

    // Function to remove highlight and restore normal appearance
    function unhighlightNode() {
      renderDesignBands(ref.current, sceneOverlayNodes, null, levelIdsRef.current, metric === 'de');
      selectedNodeRef.current = null;
    }

    // Function to zoom to a specific level
    function zoomToLevel(levelId) {
      console.log('Attempting to zoom to level:', levelId);
      
      try {
        // Remember the current selection
        const prevSelection = selectedNodeRef.current;
        
        // Create a new data object with the level property
        const newData = [{
          ...data[0],
          level: levelId
        }];
        
        // Update the chart with the new data
        Plotly.react(ref.current, newData, layout, config);
        
        // Update our tracking of the current level
        currentLevelRef.current = levelId;
        console.log('Current level set to:', currentLevelRef.current);
        
        // Reapply highlight after zooming if needed
        if (prevSelection) {
          setTimeout(() => highlightNode(prevSelection), 600);
        }
      } catch (error) {
        console.error('Error zooming to level:', error);
      }
    }

    // Function to unzoom one level up
    function unzoomOneLevel() {
      const currentId = currentLevelRef.current;
      if (!currentId) {
        console.log('Already at root level');
        return;
      }
      
      console.log('Current level:', currentId);
      
      // Find the parent of the current level
      const currentNode = nodes.find(n => n.id === currentId);
      if (!currentNode || !currentNode.parent) {
        console.log('No parent found or already at root');
        // If no parent or already at root, go to root level
        zoomToLevel('');
        return;
      }
      
      // Get the parent ID and zoom to it
      const parentId = currentNode.parent;
      console.log('Unzooming to parent:', parentId);
      zoomToLevel(parentId);
    }

    // Define the handler for sunburst clicks
    const sunburstClickHandler = ev => {
      console.log('Sunburst click event:', ev);
      
      // Extract ID and nextLevel from event data
      const id = ev?.points?.[0]?.id;
      const nextLevel = ev?.nextLevel;
      
      console.log('Clicked ID:', id, 'Next Level:', nextLevel);
      
      if (!id) return false;
      
      // Find the node object and show panel
      const nodeObj = nodes.find(n => n.id === id);
      if (nodeObj?.detailedContent) {
        // Always show the side panel on click. Pass the id so the page can keep
        // its selection state in sync (and drive the rollup ↔ node flow).
        onSelect(nodeObj.detailedContent, metric, id);

        // Highlight the selected node
        highlightNode(id);
      }
      
      // Check if this is a leaf node - don't zoom on leaves
      const isLeaf = isLeafNode(id);
      console.log(`Node ${id} is ${isLeaf ? 'a leaf' : 'not a leaf'}`);
      
      // Check for double-click using time-based detection
      const now = Date.now();
      const lastClick = clickTracker.current;
      
      if (lastClick.id === id && now - lastClick.time < 300) {
        console.log('Double click detected on:', id);
        
        // If it's a leaf node, don't do any zooming
        if (isLeaf) {
          console.log('Leaf node clicked, not zooming');
          // Still show the panel but don't zoom
        } else {
          // Check if we clicked on the center while already zoomed in
          const isCenter = id === currentLevelRef.current;
          
          if (isCenter && currentLevelRef.current) {
            // If clicked on center and already zoomed in, unzoom one level
            console.log('Center clicked while zoomed in, unzooming...');
            unzoomOneLevel();
          } else {
            // Otherwise, zoom to the clicked level
            if (nextLevel !== undefined) {
              console.log('Zooming to next level:', nextLevel);
              zoomToLevel(nextLevel);
            } else {
              console.log('No next level defined, zooming to ID:', id);
              zoomToLevel(id);
            }
          }
        }
        
        // Reset click tracker
        clickTracker.current = { id: null, time: 0, count: 0 };
      } else {
        // Update the click tracker for first click
        clickTracker.current = { id, time: now, count: 1 };
      }
      
      // Return false to prevent default zoom
      return false;
    };

    // Add the handler — skipped for a static (non-interactive) teaching ring.
    if (interactive) ref.current.on('plotly_sunburstclick', sunburstClickHandler);

    // Handle window resize events to reapply highlighting
    const handleResize = () => {
      // Re-align the design bands with Plotly's resized wedges.
      setTimeout(() => renderDesignBands(ref.current, sceneOverlayNodes, selectedNodeRef.current, levelIdsRef.current), 100);
      // If there's a selected node, reapply the highlight after resize
      if (selectedNodeRef.current) {
        setTimeout(() => highlightNode(selectedNodeRef.current), 100);
      }
    };

    window.addEventListener('resize', handleResize);

    if (animateAct1) {
      setTimeout(() => pulsePaths(ref.current, nodes, 'act-1'), 400);
    }

if (!hasInitialized.current) {
      // autoSelectRoot=false when the page uses the rollup as its home state —
      // we don't want to force the movie node into the side panel on load.
      if (autoSelectRoot) {
        const movieNode = nodes.find(n => n.id === "Movie");
        if (movieNode?.detailedContent) {
          onSelect(movieNode.detailedContent, metric, "Movie");
          // Highlight the movie node
          setTimeout(() => highlightNode("Movie"), 300);
        }
      }
      hasInitialized.current = true; // Mark as initialized
    }

    return () => {
      if (ref.current?.removeAllListeners) {
        ref.current.removeAllListeners('plotly_sunburstclick');
      }
      // Remove ONLY our afterplot handler — NOT removeAllListeners('plotly_afterplot'),
      // which would also tear out Plotly's own internal afterplot listeners (under
      // React StrictMode's mount→cleanup→remount this breaks zoom/transition).
      if (ref.current?.removeListener) {
        ref.current.removeListener('plotly_afterplot', afterPlotHandler);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [movie, includeScenes, onSelect, animateAct1, metric, dimNotOwed]);

  // External (rollup-driven) selection: when the page sets selectedId, move the
  // focus ring to that wedge without re-plotting the whole chart. Uses refs
  // populated by the main effect, so it just re-runs the overlay.
  useEffect(() => {
    if (!ref.current || !overlayNodesRef.current) return;
    selectedNodeRef.current = selectedId || null;
    renderDesignBands(ref.current, overlayNodesRef.current, selectedId || null, levelIdsRef.current, metric === 'de');
  }, [selectedId]);

  // Level highlight changed (rollup grain switched / cleared): re-render the
  // overlay so the soft level tint appears/updates without re-plotting the chart.
  useEffect(() => {
    if (!ref.current || !overlayNodesRef.current) return;
    renderDesignBands(ref.current, overlayNodesRef.current, selectedNodeRef.current, levelIds, metric === 'de');
  }, [levelIds]);

  return ref;
}

const LENS_KEYS = ['design', 'execution', 'read'];

function asList(value) {
  if (value == null || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(asList);
  if (typeof value === 'string' || typeof value === 'number') return [{ label: String(value) }];
  if (typeof value !== 'object') return [];
  if (value.label || value.move || value.rank || value.slug) return [value];
  return Object.values(value).flatMap(asList);
}

export function createCustomData(node, movie, metric, dimNotOwed = true) {
  const info = node.detailedContent;
  if (!info) return "";
  const fmt = v => fmtScore(v);   // shared: no trailing .0 on whole scores
  const wrap = (str, lim = 44) => {
    if (!str) return '';
    const words = String(str).split(' ');
    let out = '', line = '';
    words.forEach(w => { if ((line + w).length > lim) { out += line.trim() + '<br>'; line = ''; } line += w + ' '; });
    return out + line.trim();
  };

	  // Lead with the canonical node NAME ("Scene 19", "Seq 1-3") — small wheels often don't print the
	  // wedge label, so the hover is where the writer confirms which unit they're on; append the title if
	  // it adds anything beyond the name.
	  const _name = unitDisplayLabel(node, 'list') || node.label || '';
	  const _title = info.unit_title || info.title || '';   // generated orientation title wins in hover when present
	  const _norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
	  const _head = (_title && _norm(_title) !== _norm(_name)) ? (_name ? `${_name} — ${_title}` : _title) : _name;
	  let hoverText = `<b>${_head}</b>`;
  if (info.mechanism) hoverText += `  <span style="opacity:.7">(${String(info.mechanism).replace(/_/g, ' ')})</span>`;

  // ── A SPECIFIC LENS / AXIS is selected in the dropdown → the hover explains
  // THAT axis for THIS unit: its score, obligation, and the one-line note. ──
  if (metric !== 'overall') {
    const key = (METRIC_KEYS[metric] && METRIC_KEYS[metric][node.nodeType]) || metric;
    // `scores[key]` is an AXIS CELL ({score,status,obligation}) for the twelve axes, but a BARE
    // NUMBER for the aggregate keys (AnalysisController.php:2887 sets overallScore = execution as a
    // scalar). Read both shapes, or the `de` metric — which maps to overallScore, Constants.js:39 —
    // reads as undefined. Fixed 2026-08-13, landmine 76.
    const rawCell = info.scores?.[key];
    const isCell = rawCell != null && typeof rawCell === 'object';
    const score = (isCell ? rawCell.score : rawCell) ?? info[key];
    let note, status;
    if (LENS_KEYS.includes(key)) {
      // a lens (Design/Execution/Read): the verdict's one-line lens summary
      note = info.prose?.verdict?.[`${key}_summary`];
    } else {
      const card = info.prose?.axes?.[key];
      note = card?.cell_note || card?.explanation;
      status = card?.status;
    }
    // Not-owed check — MUST match the wheel colouring (prepareChartData.cellOwed): a unit that
    // doesn't owe this axis (status not_owed, or only 'light' obligation) is painted neutral, so the
    // hover must say so plainly instead of showing a score the writer would mistake for a problem.
    // Owedness is an AXIS property. Lenses and aggregates are always owed, so they must never take
    // this branch — previously `de` did, and every unit's hover read "not owed here" because the
    // bare number has no `.score` and `undefined == null` is true.
    const cellObj = (LENS_KEYS.includes(key) || !isCell) ? null : rawCell;
    const NOT_OWED_ST = ['not_owed', 'na', 'not_relevant', 'not_applicable'];
    const WEAK_OBL = ['light', 'none', 'na', 'not_owed'];
    const notOwed = dimNotOwed && !!cellObj && (cellObj.score == null
      || NOT_OWED_ST.includes(cellObj.status) || WEAK_OBL.includes(cellObj.obligation));

    if (notOwed) {
      hoverText += `<br><b>${METRIC_LABELS[metric] || key}</b> <span style="opacity:.75">— not owed here</span>`;
      hoverText += `<br><em>This ${node.nodeType} isn’t really about ${(METRIC_LABELS[metric] || key).toLowerCase()}, so it isn’t scored on it — a faint colour here is normal, not a problem.</em>`;
    } else {
      hoverText += `<br><b>${METRIC_LABELS[metric] || key}</b> ${score != null ? fmt(score) + '/10' : '—'}`;
      if (status) hoverText += `  <span style="opacity:.6">· ${String(status).replace(/_/g, ' ')}</span>`;
      if (note) hoverText += `<br><em>${wrap(note)}</em>`;
      else hoverText += `<br><span style="opacity:.55">no note for this ${LENS_KEYS.includes(key) ? 'lens' : 'axis'} yet</span>`;
    }
    if (node.nodeType === 'scene' && info.page_count) hoverText += `<br><span style="opacity:.7">Pages: ${fmt(info.page_count)}</span>`;
    return hoverText;
  }

  // ── OVERALL: triage language, NOT scores — band + the three lens statuses +
  // the single biggest lever. Three lines max; no decimals (that's the click). ──
  const v = verdictFromLenses(
    { design: info.design, execution: info.execution, read: info.read },
    info.type === 'movie' ? 'script' : info.type);
  if (v.band) {
    const bs = BAND_STYLE[v.band];
    hoverText += `<br><b style="color:${bs ? bs.fg : '#0f172a'}">${String(v.label || '').toUpperCase()}</b>`;
    const w = k => (v.statuses[k] ? v.statuses[k] : '—');
    // ✓ where the group is solid, the score only where it is not (owner, 2026-10-14) — the eye goes to the problem.
    const g = k => (v.statuses[k] === 'solid' ? '✓' : (info[k] != null ? String(Math.round(info[k] * 10) / 10) : w(k)));
    hoverText += `<br>Design ${g('design')} · Execution ${g('execution')} · Involvement ${g('read')}`;
	    const reps = asList(info.prose?.repair).sort((a, b) => (a.rank || 9) - (b.rank || 9));
    let lever = reps[0] && reps[0].label;
    if (!lever) {
      const weakest = [['Design', info.design], ['Execution', info.execution], ['Involvement', info.read]]
        .filter(x => x[1] != null).sort((a, b) => a[1] - b[1])[0];
      lever = weakest ? `lift ${weakest[0]}` : null;
    }
    if (lever) hoverText += `<br><b>Biggest gain:</b> ${wrap(lever)}`;
  } else if (info.design == null) {
    hoverText += `<br><span style="opacity:.6">not analyzed yet</span>`;
  }
  if (node.nodeType === 'scene' && info.page_count) hoverText += `<br><span style="opacity:.7">Pages: ${fmt(info.page_count)}</span>`;
  return hoverText;
}

function label(r) {
  return ["Pass", "Consider", "Recommend", "Highly Recommend"][r] ?? `Rating ${r}`;
}

function pulsePaths(el, nodes, rootId) {
  if (!el) return; // <-- ADD THIS LINE
  const ids = nodes.map(n => n.id);
  const paths = el.querySelectorAll('.slice path');
  paths.forEach((p, i) => {
    if (ids[i] && ids[i].startsWith(rootId)) p.classList.add('pulse');
  });
  setTimeout(() => paths.forEach(p => p.classList.remove('pulse')), 3500);
}
